import { StyleSheet, Text, View, ScrollView, Linking } from 'react-native';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import PageTitle from '../../components/PageTitle';
import { Footer } from '../../components/Footer';
import DropDown from '../../components/DropDown/DropDown';
import { STYLE_VARS } from '../../constants/style-vars';
import { Button } from '../../components/Button';
import { ReciprocalClub, ReciprocalClubProps } from './types';
import ReciprocalClubItem from './components/ReciprocalClubItem';
import { useIsDesktop } from '../../hooks/useIsDeskTop';
import { ClubServiceLayout } from '../../WebLayout';
import { IS_WEB } from '../../constants/platform';
import { addBase64Prefix } from '../../services/addBase64Prefix';
import {
  food_beverages_event,
  reciprocal_clubs,
  tennis_sport_icon,
} from '../../assets/base64';
import { COLORS, FONTS } from '../../constants';
import ServicesApi, {
  GetReciprocalClubProps,
} from '../../services/api/services.api';
import { IClub, ICountry } from '../../types/services.types';
import CountryDropDown from '../../components/CountryDropDown';
import ClubDropDown from '../../components/ClubDropDown';
import { Loading } from '../../components';

const ReciprocalClubs: FC<ReciprocalClubProps> = ({ navigation, route }) => {
  const topDescription =
    'KCC MEMBERS CAN ENJOY RECIPROCAL PRIVILGES AT THE CLUBS LISTED BELOW.';
  const isDesktop = useIsDesktop();
  const desc =
    'IF YOU WANT TO APPLY FOR A RECIPROCAL MEMBERSHIP FOR ANY OF THE CLUBS LISTED, PLEASE SEND US AN EMAIL BY CLICKING THE BUTTON BELOW GIVING US:';
  const [countries, setCountries] = useState<ICountry[]>([]);
  const [selectClubs, setSelectClubs] = useState<IClub[]>([]);
  const [clubs, setClubs] = useState<IClub[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [total, setTotal] = useState<number>(0);
  const [selectedCountry, setSelectedCountry] = useState<ICountry>();
  const [selectedClub, setSelectedClub] = useState<IClub>();

  const getCountries = useCallback(async () => {
    try {
      setSelectedCountry(undefined);
      const res = await ServicesApi.getCountries();
      setCountries(res.data);
    } catch (error: any) {
      console.log(error);
    }
  }, []);

  const getSelectClubs = useCallback(async (props: GetReciprocalClubProps) => {
    try {
      const res = await ServicesApi.getAllReciprocalClubs(props);
      setSelectClubs(res.data);
    } catch (error: any) {
      console.log(error);
    }
  }, []);

  const getClubs = useCallback(async (props: GetReciprocalClubProps) => {
    try {
      setIsLoading(true);
      const res = await ServicesApi.getReciprocalClubs(props);
      setClubs(res.data.reciprocal_clubs);
      setTotal(res.data.total);
    } catch (error: any) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    getCountries();
  }, [getCountries]);

  useEffect(() => {
    setSelectClubs([]);
    getSelectClubs({ country: selectedCountry?.slug });
    setClubs([]);
    getClubs({ country: selectedCountry?.slug });
  }, [selectedCountry, getSelectClubs, getClubs]);

  const handleLoadMore = useCallback(async () => {
    if (clubs.length < total) {
      try {
        setIsLoading(true);
        const res = await ServicesApi.getReciprocalClubs({
          page: Math.ceil(clubs.length / 5) + 1,
          country: selectedCountry?.slug,
        });
        setClubs([...clubs, ...res.data.reciprocal_clubs]);
        setTotal(res.data.total);
      } catch (error: any) {
        console.log(error);
      } finally {
        setIsLoading(false);
      }
    }
  }, [total, clubs, selectedCountry]);

  const handleSetSelectedClub = useCallback((selectClubs: IClub) => {
    setSelectedClub(selectClubs);
    setClubs([selectClubs]);
    setTotal(1);
  }, []);

  const renderDescList = useMemo(() => {
    const infoList = [
      {
        number: 1,
        content: 'YOUR FULL NAME',
      },
      {
        number: 2,
        content: 'YOUR KCC ACCOUNT NO.',
      },
      {
        number: 3,
        content: 'THE CLUB(S) YOU WOULD LIKE TO VISIT',
      },
      {
        number: 4,
        content: 'THE DATES YOU WILL BE VISITING',
      },
      {
        number: 5,
        content: 'FULL NAME OF ANY FAMILY MEMBER(S) WHO ARE VISITING WITH YOU',
      },
    ];

    return (
      <View
        style={{
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {infoList.map((info) => (
          <Text
            style={{
              textAlign: 'center',
              fontFamily: FONTS.AvenirMedium,
              fontSize: isDesktop() ? 15 : 13,
              fontWeight: '500',
              color: COLORS.DARKER_GREY,
              lineHeight: 20,
            }}
          >{`${info.number}. ${info.content}`}</Text>
        ))}
      </View>
    );
  }, [isDesktop]);

  if (isDesktop()) {
    return (
      <ClubServiceLayout
        title="Reciprocal Clubs"
        icon={
          IS_WEB
            ? addBase64Prefix(reciprocal_clubs)
            : require('../../assets/reciprocal-clubs.png')
        }
        description={topDescription}
        current={3}
      >
        <View
          style={[styles.row, { justifyContent: 'space-between', zIndex: 1 }]}
        >
          <View style={[styles.row, { width: '45%' }]}>
            <View style={{ width: '50%' }}>
              <Text style={[styles.text, { paddingRight: 10 }]}>
                Browse Countries
              </Text>
            </View>
            <View style={{ width: '50%' }}>
              <CountryDropDown
                items={countries}
                dropdownStyle={styles.dropdownStyle}
                placeholder="Select Country"
                customDownButton={require('../../assets/down-arrow.png')}
                customUpButton={require('../../assets/up-arrow.png')}
                setValues={setSelectedCountry}
              />
            </View>
          </View>
          <View style={[styles.row, { width: '45%' }]}>
            <View style={{ width: '50%' }}>
              <Text style={[styles.text, { paddingRight: 10 }]}>
                Browse Reciprocal Clubs
              </Text>
            </View>
            <View style={{ width: '50%' }}>
              <ClubDropDown
                items={selectClubs}
                dropdownStyle={styles.dropdownStyle}
                placeholder="Select Club"
                customDownButton={require('../../assets/down-arrow.png')}
                customUpButton={require('../../assets/up-arrow.png')}
                setValues={handleSetSelectedClub}
              />
            </View>
          </View>
        </View>
        <Text
          style={{
            textAlign: 'center',
            fontFamily: FONTS.AvenirMedium,
            fontSize: 15,
            fontWeight: '500',
            color: COLORS.DARKER_GREY,
            lineHeight: 20,
          }}
        >
          {desc}
        </Text>
        {renderDescList}
        <View
          style={{ paddingBottom: 40, alignSelf: 'center', paddingTop: 20 }}
        >
          <Button
            text="EMAIL US"
            style={STYLE_VARS.blueButton}
            onPress={() => {
              Linking.openURL('mailto:MEMBERSHIP@KCC.ORG.HK');
            }}
          />
        </View>
        <View style={{}}>
          {/* <Text style={styles.title}>AUSTRALIA</Text> */}
          {clubs.map((reciprocal) => (
            <ReciprocalClubItem item={reciprocal} key={reciprocal.id} />
          ))}
        </View>
        {isLoading === false && clubs.length === 0 ? (
          <View style={styles.noDataWrapper}>
            <Text style={[styles.noDataText, { fontSize: 20 }]}>
              THERE ARE CURRENTLY NO ClUBS
            </Text>
          </View>
        ) : (
          <></>
        )}
        {isLoading && <Loading style={{ paddingVertical: 30 }} />}
        {total > clubs.length && (
          <View
            style={{ marginTop: 30, marginBottom: 60, alignSelf: 'center' }}
          >
            <Button
              text="VIEW MORE"
              style={STYLE_VARS.blueButton}
              onPress={handleLoadMore}
              disabled={isLoading}
            />
          </View>
        )}
      </ClubServiceLayout>
    );
  }
  return (
    <ScrollView showsVerticalScrollIndicator={false}>
      <View style={styles.container}>
        <PageTitle
          title="Reciprocal Clubs"
          icon={require('../../assets/reciprocal-clubs.png')}
          description={topDescription}
        />
      </View>
      <View style={styles.container}>
        <View style={styles.row}>
          <View style={{ width: '50%' }}>
            <Text style={[styles.text, { paddingRight: 10 }]}>
              Browse Countries
            </Text>
          </View>
          <View style={{ width: '50%' }}>
            <CountryDropDown
              items={countries}
              dropdownStyle={styles.dropdownStyle}
              placeholder="Select Country"
              customDownButton={require('../../assets/down-arrow.png')}
              customUpButton={require('../../assets/up-arrow.png')}
              title="Please Select Country"
              setValues={setSelectedCountry}
            />
          </View>
        </View>
        <View style={styles.row}>
          <View style={{ width: '50%' }}>
            <Text style={[styles.text, { paddingRight: 10 }]}>
              Browse Reciprocal Clubs
            </Text>
          </View>
          <View style={{ width: '50%' }}>
            <ClubDropDown
              items={selectClubs}
              dropdownStyle={styles.dropdownStyle}
              placeholder="Select Club"
              customDownButton={require('../../assets/down-arrow.png')}
              customUpButton={require('../../assets/up-arrow.png')}
              title="Please Select Club"
              setValues={handleSetSelectedClub}
            />
          </View>
        </View>
      </View>
      <View style={styles.container}>
        <Text
          style={{
            textAlign: 'center',
            fontFamily: FONTS.AvenirMedium,
            fontSize: 13,
            fontWeight: '500',
            color: COLORS.DARKER_GREY,
            lineHeight: 20,
          }}
        >
          {desc}
        </Text>
        {renderDescList}
        {/* <View style={{ paddingBottom: 30, alignSelf: 'flex-start' }}>
          <Button
            text="APPLY FOR RECIPROCAL CLUB"
            style={STYLE_VARS.blueButton}
            onPress={() => {
              navigation.push('ApplyReciprocalClub');
            }}
          />
        </View> */}
        <View
          style={{ paddingBottom: 30, alignSelf: 'center', paddingTop: 15 }}
        >
          <Button
            text="EMAIL US"
            style={STYLE_VARS.blueButton}
            onPress={() => {
              Linking.openURL('mailto:MEMBERSHIP@KCC.ORG.HK');
            }}
          />
        </View>
      </View>
      <View style={styles.container}>
        {/* <Text style={styles.title}>AUSTRALIA</Text> */}
        {clubs.map((reciprocal) => (
          <ReciprocalClubItem item={reciprocal} key={reciprocal.id} />
        ))}
      </View>
      {isLoading === false && clubs.length === 0 ? (
        <View style={styles.noDataWrapper}>
          <Text style={[styles.noDataText, { fontSize: 15 }]}>
            THERE ARE CURRENTLY NO ClUBS
          </Text>
        </View>
      ) : (
        <></>
      )}
      {isLoading && <Loading style={{ paddingVertical: 30 }} />}
      {total > clubs.length && (
        <View style={{ marginTop: 30, marginBottom: 60 }}>
          <Button
            text="VIEW MORE"
            style={STYLE_VARS.blueButton}
            onPress={handleLoadMore}
            disabled={isLoading}
          />
        </View>
      )}
      <Footer />
    </ScrollView>
  );
};

export default ReciprocalClubs;

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 20,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
  },
  dropdownStyle: {
    borderRadius: 5,
  },
  title: {
    fontSize: 20,
    fontFamily: FONTS.IowanOldStyleBTRoman,
    fontWeight: '600',
    color: STYLE_VARS.textColor,
    letterSpacing: 1,
    marginBottom: 20,
  },
  text: {
    fontFamily: FONTS.AvenirMedium,
    color: STYLE_VARS.textColor,
    fontSize: 13,
    fontWeight: '500',
    textTransform: 'uppercase',
  },
  noDataWrapper: {
    flexDirection: 'row',
    justifyContent: 'center',
    paddingVertical: 20,
  },
  noDataText: {
    color: COLORS.GREEN,
    textTransform: 'uppercase',
    fontFamily: FONTS.AvenirMedium,
  },
});
